import Carousel from '@allesauto/core/carousel';
import Headline from '@allesauto/core/headline';
import { Colors, theme } from '@allesauto/utils/consts';
import { sbEditable } from '@storyblok/storyblok-editable';
import clsx from 'clsx';
import React, { useEffect, useState } from 'react';

import 'yet-another-react-lightbox/styles.css';

import ImageLogoItem, { ImageLogoItemProps } from './ImageLogoItem';

interface ImageLogoSliderProps {
  headline?: string;
  headlineTag?: 2 | 3 | 4;
  headlineFontSize?: 'title' | 'headline2' | 'headline3' | 'headline4';
  headlineColor: Colors;
  background: Colors;
  items?: ImageLogoItemProps[];
  _uid: string;
  component: string;
}

const responsiveCarouselBreakpoints = {
  desktopUltraWide: {
    breakpoint: { max: 9999, min: 1875 },
    items: 6,
    slidesToSlide: 2,
    partialVisibilityGutter: 0,
  },
  desktopSuperLarge: {
    breakpoint: { max: 1874, min: 1600 },
    items: 5,
    slidesToSlide: 2,
    partialVisibilityGutter: 0,
  },
  desktopLarge: {
    breakpoint: { max: 1599, min: 1300 },
    items: 4,
    slidesToSlide: 2,
    partialVisibilityGutter: 0,
  },
  desktop: {
    breakpoint: { max: 1299, min: 1000 },
    items: 3,
    slidesToSlide: 3,
    partialVisibilityGutter: 0,
  },
};

const cssClasses = {
  container:
    'pt-[32px] pb-[40px] px-[16px] lg:px-[76px] lg:pt-[70px] lg:pb-[98.5px]',
  nextButton: clsx('!p-0 !rounded-circle h-[28px] w-[28px]'),
  mobileList: 'flex gap-[10px] hide-scrollbar overflow-y-scroll lg:hidden',
  headline: 'sm:mb-[30px] lg:mb-0',
};

const ImageLogoSlider = (props: ImageLogoSliderProps) => {
  const {
    headline,
    headlineColor = Colors.PURPLE,
    headlineTag = 2,
    headlineFontSize = 'headline2',
    background,
    items,
  } = props;
  const [showDots, setShowDots] = useState(false);

  useEffect(() => {
    const checkBreakpoint = () => {
      const responsiveBreakpointList = Object.values(
        responsiveCarouselBreakpoints
      );

      responsiveBreakpointList.forEach(
        ({ breakpoint: breakpoints, items: itemsToShow }) => {
          if (
            window.innerWidth > breakpoints.min &&
            window.innerWidth < breakpoints.max
          ) {
            if (items?.length) setShowDots(items.length > itemsToShow);
          }
        }
      );
    };

    checkBreakpoint();
    window.addEventListener('resize', checkBreakpoint);
    return () => {
      window.removeEventListener('resize', checkBreakpoint);
    };
  }, [items?.length]);

  const carouselClasses = {
    sectionClass: 'bg-transparent sm:hidden lg:grid !pb-0',
    containerClass: '!col-start-2 !col-end-24 md:pb-60px lg:pb-80px',
    dotListClass: clsx(
      'react-multi-carousel-dot-dark-bg',
      '!mb-0 gap-10px p-30px'
    ),
    itemClass: 'lg:pl-10px xl:pl-[15px]',
    buttonLeftClass: clsx('lg:col-start-1', cssClasses.nextButton),
    buttonRightClass: clsx('lg:col-end-25', cssClasses.nextButton),
  };

  return (
    <section
      className={clsx(cssClasses.container, theme[background].background)}
      {...sbEditable(props)}
    >
      {headline && (
        <Headline
          headlineTag={headlineTag}
          headlineText={headline}
          headlineColor={theme[headlineColor].text}
          headlineClassName={clsx(
            cssClasses.headline,
            `text-${headlineFontSize}`
          )}
        />
      )}
      <div className={cssClasses.mobileList}>
        {items?.map((item) => (
          <ImageLogoItem key={item._uid} {...item} />
        ))}
      </div>
      <Carousel
        isInfinite={false}
        responsive={responsiveCarouselBreakpoints}
        darkBackground={background === Colors.PURPLE}
        classNames={carouselClasses}
        showDots={showDots}
        arrows={showDots}
      >
        {items?.map((item) => (
          <ImageLogoItem key={item._uid} {...item} />
        ))}
      </Carousel>
    </section>
  );
};

export default ImageLogoSlider;
